import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { setModalIsOpened } from "@/core/store/app";
import { Idea } from "../../model/types";
import "./ConfirmationModals.scss";

interface ConfirmationModalsProps {
  ideaId: string | undefined;
  selectedIdea: Idea;
  user: any;
  ideaActions: any;
}

interface ModalConfig {
  visible: boolean;
  title: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  description: string;
  confirmLabel: string;
  loadingLabel: string;
  isLoading: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

export const ConfirmationModals: React.FC<ConfirmationModalsProps> = ({
  ideaId,
  selectedIdea,
  user,
  ideaActions,
}) => {
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const close = () => {
    setActiveModal(null);
    setModalIsOpened(false);
  };

  const open = (name: string) => {
    setActiveModal(name);
    setModalIsOpened(true);
  };

  React.useEffect(() => {
    (window as any).showRequestConfirmModal = () => open("request");
    (window as any).showLeaveConfirmModal = () => open("leave");
    (window as any).showWithdrawConfirmModal = () => open("withdraw");
    (window as any).showArchiveConfirmModal = () => open("archive");
    (window as any).showPublishConfirmModal = () => open("publish");
    (window as any).showRestoreConfirmModal = () => open("restore");

    return () => {
      [
        "showRequestConfirmModal",
        "showLeaveConfirmModal",
        "showWithdrawConfirmModal",
        "showArchiveConfirmModal",
        "showPublishConfirmModal",
        "showRestoreConfirmModal",
      ].forEach((key) => delete (window as any)[key]);
    };
  }, []);

  const modals: Record<string, ModalConfig> = {
    request: {
      visible: activeModal === "request",
      title: "Request to Join Idea",
      icon: "icon-user-plus",
      iconBg: "#EEF2FF",
      iconColor: "#4F46E5",
      description:
        "The idea owner will be notified and can accept or decline your request.",
      confirmLabel: "Request to Join",
      loadingLabel: "Requesting...",
      isLoading: ideaActions.requestLoading,
      onConfirm: async () => {
        await ideaActions.requestToJoin();
        close();
      },
      onClose: close,
    },
    leave: {
      visible: activeModal === "leave",
      title: "Leave Idea",
      icon: "icon-leave",
      iconBg: "#FEE2E2",
      iconColor: "#DC2626",
      description:
        "Are you sure you want to leave this idea? You will no longer have access to the partnership details and discussions.",
      confirmLabel: "Leave Idea",
      loadingLabel: "Leaving...",
      isLoading: ideaActions.requestLoading,
      onConfirm: async () => {
        await ideaActions.leaveIdea();
        close();
      },
      onClose: close,
    },
    withdraw: {
      visible: activeModal === "withdraw",
      title: "Withdraw Request",
      icon: "icon-withdraw",
      iconBg: "#FEE2E2",
      iconColor: "#DC2626",
      description:
        "Are you sure you want to withdraw your request to join this idea? This action cannot be undone.",
      confirmLabel: "Withdraw Request",
      loadingLabel: "Withdrawing...",
      isLoading: ideaActions.requestLoading,
      onConfirm: async () => {
        await ideaActions.withdrawRequest();
        close();
      },
      onClose: close,
    },
    archive: {
      visible: activeModal === "archive",
      title: "Archive Idea",
      icon: "icon-archive",
      iconBg: "#FEF3C7",
      iconColor: "#D97706",
      description:
        "Are you sure you want to archive this idea? You can restore it later if needed.",
      confirmLabel: "Archive Idea",
      loadingLabel: "Archiving...",
      isLoading: ideaActions.archiveLoading,
      onConfirm: async () => {
        await ideaActions.archiveIdea();
        close();
      },
      onClose: close,
    },
    publish: {
      visible: activeModal === "publish",
      title: "Publish Idea",
      icon: "icon-globe",
      iconBg: "#DCFCE7",
      iconColor: "#16A34A",
      description:
        "Are you sure you want to publish this idea? It will be visible to all users and they will be able to request to join.",
      confirmLabel: "Publish Idea",
      loadingLabel: "Publishing...",
      isLoading: ideaActions.publishLoading,
      onConfirm: async () => {
        await ideaActions.publishIdea();
        close();
      },
      onClose: close,
    },
    restore: {
      visible: activeModal === "restore",
      title: "Restore Idea",
      icon: "icon-restore",
      iconBg: "#DCFCE7",
      iconColor: "#16A34A",
      description: "Are you sure you want to restore this idea?",
      confirmLabel: "Restore Idea",
      loadingLabel: "Restoring...",
      isLoading: ideaActions.restoreLoading,
      onConfirm: async () => {
        await ideaActions.restoreIdea();
        close();
      },
      onClose: close,
    },
  };

  const current = activeModal ? modals[activeModal] : null;

  if (!current) return null;

  return (
    <Dialog
      visible={current.visible}
      onHide={current.onClose}
      header={null}
      footer={null}
      closable={false}
      modal
      dismissableMask
      appendTo={document.body}
      blockScroll
      className="confirmation-dialog"
      style={{ width: "440px", borderRadius: "16px" }}
    >
      <div className="confirmation-dialog__body">
        {/* Close button */}
        <button
          className="confirmation-dialog__close"
          onClick={current.onClose}
        >
          <i className="pi pi-times" />
        </button>

        {/* Icon */}
        {/* <div
          className="confirmation-dialog__icon-wrap"
          style={{ backgroundColor: current.iconBg }}
        >
          <i className={current.icon} style={{ color: current.iconColor }} />
        </div> */}

        {/* Content */}
        <h3 className="confirmation-dialog__title">{current.title}</h3>
        <p className="confirmation-dialog__description">
          {current.description}
        </p>

        {/* Actions */}
        <div className="confirmation-dialog__actions">
          <button
            className="confirmation-dialog__btn confirmation-dialog__btn--cancel"
            onClick={current.onClose}
            disabled={current.isLoading}
          >
            Cancel
          </button>
          <button
            className="confirmation-dialog__btn confirmation-dialog__btn--confirm"
            onClick={current.onConfirm}
            disabled={current.isLoading}
          >
            {current.isLoading ? current.loadingLabel : current.confirmLabel}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
